/**
 * The bills behind one person's line in "Put on the tab".
 *
 * The panel says somebody put Rs 13,639 on their account in three bills. This
 * is the three bills: which, when, and what was on each — the answer to "what
 * did he actually eat", without leaving the report.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR, formatServerTime } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { BillSheet } from '../pos/BillSheet.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

const TYPE = { dine_in: 'Dine-in', takeaway: 'Takeaway', delivery: 'Delivery' };

export function TabBillsSheet({ person, from, to, onClose }) {
  // The bill being looked at as a receipt, to print. The same sheet the Orders
  // page opens, so what prints here is the bill the customer would have had.
  const [printing, setPrinting] = useState(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['reports', 'staff-tab-bills', person.id, from, to],
    queryFn: () => api.get(withQuery(`/reports/staff-tab/${person.id}/bills`, { from, to })),
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${person.name} — bills on account`}
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
      onClick={(e) => {
        // Outside the sheet closes it; inside does not.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-surface p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-semibold text-lg text-ink">{person.name}</h2>
            <p className="text-sm text-ink-2">Put on account in the dates chosen</p>
          </div>
          {data && (
            <p className="shrink-0 text-lg font-semibold tabular-nums text-ink">
              {formatPKR(data.total)}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner label="Loading" />
          </div>
        ) : isError ? (
          <p role="alert" className="py-10 text-center text-sm text-bad">
            {error?.message ?? 'Could not load these bills.'}
          </p>
        ) : data.bills.length === 0 ? (
          <p className="py-10 text-center text-sm text-ink-3">No bills in these dates.</p>
        ) : (
          <ul className="mt-3">
            {data.bills.map((bill) => (
              <li key={bill.orderId} className="border-t border-rule py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="min-w-0 text-sm">
                    <span className="font-medium text-ink">{bill.orderNumber}</span>
                    <span className="text-ink-3">
                      {' '}
                      · {formatServerTime(bill.createdAt)}
                    </span>
                  </p>
                  <p className="flex shrink-0 items-baseline gap-2">
                    <span className="font-medium tabular-nums text-ink">
                      {formatPKR(bill.amount)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPrinting(bill.orderId)}
                      aria-label={`Print bill ${bill.orderNumber}`}
                      className="rounded-lg px-2 py-1 text-xs font-medium text-brand-300 hover:bg-brand-400/15"
                    >
                      Print
                    </button>
                  </p>
                </div>
                <p className="mt-0.5 text-xs text-ink-3">
                  {[
                    TYPE[bill.orderType] ?? bill.orderType,
                    bill.tableNumber ? `Table ${bill.tableNumber}` : null,
                    bill.recordedBy ? `by ${bill.recordedBy}` : null,
                    bill.refunded > 0 ? `${formatPKR(bill.refunded)} refunded` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>

                <table className="mt-2 w-full text-sm">
                  <tbody>
                    {bill.items.map((item, index) => (
                      <tr
                        key={index}
                        className={item.refunded ? 'text-ink-3 line-through' : 'text-ink-2'}
                      >
                        <td className="w-10 py-0.5 pr-2 align-top tabular-nums">
                          {item.quantity} ×
                        </td>
                        <td className="py-0.5 pr-2">
                          {item.name}
                          {item.notes ? <span className="text-ink-3"> — {item.notes}</span> : null}
                        </td>
                        <td className="py-0.5 text-right align-top tabular-nums">
                          {formatPKR(item.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </li>
            ))}
          </ul>
        )}

        <Button variant="secondary" size="lg" className="mt-4 w-full" onClick={onClose}>
          Close
        </Button>
      </div>

      {/* Opened for looking at and printing only — no refund or discount from a
          report. Closing it comes back to the list of bills. */}
      {printing && (
        <BillSheet orderId={printing} canRefund={false} onClose={() => setPrinting(null)} />
      )}
    </div>
  );
}
